import { useEffect, forwardRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import {
  createCategoryAxis,
  createLegend,
  createLineSeries,
  createBarSeries,
  createBarAxis,
  createLineAxis,
} from "./utils";
import { BarAndLineChartDataProps, BarSize } from "./types";
import { BaseChartColors } from "../constants";
import { configureNumberFormatter } from "../utils";

interface BarAndLineChartProps {
  id: string;
  chartData: BarAndLineChartDataProps[];
  xAxisLabel: string;
  barSeriesColor: BaseChartColors;
  barSeriesName: string;
  lineSeriesName: string;
  /** Controls the size of the bars */
  barSize?: BarSize;
}

export const BarAndLineChart = forwardRef<
  am4charts.XYChart,
  BarAndLineChartProps
>((props, ref) => {
  useEffect(() => {
    const chart = am4core.create(props.id, am4charts.XYChart);
    if (typeof ref === "function") {
      ref(chart);
    } else if (ref) {
      ref.current = chart;
    }

    configureNumberFormatter(chart);

    chart.paddingTop = 20;
    chart.paddingLeft = 0;
    chart.paddingRight = 64;
    chart.paddingBottom = 0;

    chart.data = [...props.chartData].reverse();

    createCategoryAxis(chart);

    createLegend(chart);
    const barAxis = createBarAxis(chart, props.xAxisLabel);
    createBarSeries(
      chart,
      props.barSeriesColor,
      props.barSeriesName,
      props.barSize
    );

    const lineAxis = createLineAxis(chart, barAxis);
    createLineSeries(chart, props.lineSeriesName, lineAxis);

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "none";

    chart.cursor.lineX.disabled = true;
    chart.cursor.lineY.disabled = true;

    return () => {
      chart.dispose();
    };
  }, [
    props.barSeriesColor,
    props.barSeriesName,
    props.chartData,
    props.id,
    props.lineSeriesName,
    props.xAxisLabel,
    props.barSize,
    ref,
  ]);

  return <div id={props.id} style={{ width: "100%", height: "100%" }}></div>;
});
